import { useFormikContext } from 'formik';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import { buildMCPConfig } from '@/services/tool.service';

import { useCreateToolMutation } from '@/query/tool.query';

import type { CreateToolFormValues } from '@/schemas/tool.schema';

import { useFormikError } from '@/lib/error';

import { MCPFields } from './tool-form/mcp-fields';

import { CreateToolFormProvider } from '@/formik/tool.formik';
import useModalStore from '@/store/modal.store';
import { useToolsStore } from '@/store/tools.store';
import { TOOL_TYPE, type ToolStatus } from '@/types/tool.type';

function CreateToolFields({ isPending }: { isPending: boolean }) {
  const closeModal = useModalStore(state => state.closeModal);
  const { getFieldProps, values, setFieldValue, isValid } =
    useFormikContext<CreateToolFormValues>();

  const hasError = useFormikError();

  return (
    <div className="flex max-h-[75vh] flex-col">
      <div className="space-y-5 overflow-y-auto py-1 pr-1">
        <section className="space-y-3 px-1">
          <div className="space-y-1">
            <p className="text-sm font-medium">Tool Details</p>
            <p className="text-xs text-muted-foreground">
              Define the MCP tool name and describe when the agent should use it.
            </p>
          </div>

          <div className="space-y-2">
            <Field aria-invalid={hasError('name')}>
              <RequiredLabel htmlFor="tool-name">Tool Name</RequiredLabel>
              <Input
                id="tool-name"
                placeholder="e.g. Patient Lookup"
                {...getFieldProps('name')}
                disabled={isPending}
                aria-invalid={hasError('name')}
              />
              <ErrorField name="name" />
            </Field>
          </div>

          <div className="space-y-2">
            <Field aria-invalid={hasError('description')}>
              <Label htmlFor="tool-description">Description</Label>
              <Textarea
                id="tool-description"
                placeholder="Describe what this tool does and when the agent should call it..."
                rows={4}
                {...getFieldProps('description')}
                disabled={isPending}
                className="min-h-[88px] resize-none"
                aria-invalid={hasError('description')}
              />
              <ErrorField name="description" />
            </Field>
          </div>
        </section>

        <section className="space-y-3 px-1">
          <div className="space-y-1">
            <p className="text-sm font-medium">Availability</p>
            <p className="text-xs text-muted-foreground">
              Adjust whether the tool is currently enabled for use.
            </p>
          </div>
          <div className="flex h-9 w-fit items-center gap-3 rounded-md border px-3">
            <Switch
              id="tool-enabled"
              checked={values.enabled}
              onCheckedChange={val => setFieldValue('enabled', val)}
              disabled={isPending}
            />
            <Label htmlFor="tool-enabled">Enabled</Label>
          </div>
        </section>

        <section className="space-y-4 px-1">
          <div className="space-y-1">
            <p className="text-sm font-medium">Connection</p>
            <p className="text-xs text-muted-foreground">
              Configure the MCP server endpoint, request timeouts, and request metadata.
            </p>
          </div>

          <MCPFields isPending={isPending} />
        </section>
      </div>

      <DialogFooter className="mt-4 border-t pt-4">
        <Button type="button" variant="outline" onClick={closeModal} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!isValid || isPending}>
          {isPending ? 'Creating...' : 'Create Tool'}
        </Button>
      </DialogFooter>
    </div>
  );
}

export function CreateToolDialog() {
  const selectTool = useToolsStore(state => state.selectTool);
  const closeModal = useModalStore(state => state.closeModal);

  const mutation = useCreateToolMutation({
    onSuccess: tool => {
      selectTool(tool);
      closeModal();
    },
  });

  const handleSubmit = (values: CreateToolFormValues) => {
    const config = values.type === 'MCP' ? buildMCPConfig(values) : null;

    mutation.mutate({
      name: values.name,
      description: values.description,
      type: TOOL_TYPE.MCP,
      status: values.status as ToolStatus,
      enabled: values.enabled,
      config,
    });
  };

  return (
    <Dialog open={true}>
      <DialogHeader>
        <DialogTitle>Add Tool</DialogTitle>
        <DialogDescription>Create a new MCP tool connection for the agent.</DialogDescription>
      </DialogHeader>
      <CreateToolFormProvider onSubmit={handleSubmit}>
        <CreateToolFields isPending={mutation.isPending} />
      </CreateToolFormProvider>
    </Dialog>
  );
}
